import { cn } from "@/lib/utils";

/**
 * Colored delta display — ▲/▼ + currency-formatted absolute value, color
 * keyed to direction. Null and near-zero values render as a muted em-dash so
 * floating-point noise doesn't flash green/red.
 *
 * `invert` flips the color semantics (not the arrow) for metrics where UP is
 * BAD — spend vs budget, churn, cost per unit. The arrow always reports the
 * direction of the number; the color reports whether that's good news.
 */
export function DeltaCell({
  value,
  threshold = 1,
  format,
  invert = false,
  chip = false,
  emptyLabel = "—",
  className,
}: {
  value: number | null | undefined;
  /** Below this absolute value, render as em-dash. Default $1. */
  threshold?: number;
  /** Custom formatter for the magnitude. Defaults to fmtCur. */
  format?: (n: number) => string;
  /** Positive = bad (red), negative = good (green). For spend-like metrics. */
  invert?: boolean;
  /**
   * Render as a tinted pill instead of bare colored text. Use inside tiles and
   * dense tables where the delta needs to read as its own status object rather
   * than as part of the number beside it. Light-tinted per the chip convention
   * — deltas are informational, not alarms.
   */
  chip?: boolean;
  /** What to show when there's nothing to compare. Default em-dash. */
  emptyLabel?: React.ReactNode;
  className?: string;
}) {
  if (value == null || Math.abs(value) < threshold) {
    return (
      <span className={cn("text-muted-foreground", className)}>{emptyLabel}</span>
    );
  }
  const positive = value > 0;
  const good = invert ? !positive : positive;
  const fmt = format ?? defaultFmt;
  const body = (
    <>
      {positive ? "▲" : "▼"} {fmt(Math.abs(value))}
    </>
  );
  if (chip) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
          good ? "bg-success-light text-success" : "bg-danger-light text-danger",
          className,
        )}
      >
        {body}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "tabular-nums font-semibold",
        good ? "text-success" : "text-danger",
        className,
      )}
    >
      {body}
    </span>
  );
}

function defaultFmt(v: number): string {
  if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toFixed(2) + "M";
  if (Math.abs(v) >= 10_000) return "$" + (v / 1_000).toFixed(1) + "K";
  return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
